import { type FunctionComponent } from 'react';

import { Image } from '@/core/Image/Image';
import { isB2B } from '@/core/settings/utils';
import { twMerge } from '@/core/tamagoshiTailwind/util/twMerge';
import {
  BREAKPOINTS,
  SIZES,
} from '@/domains/productDiscovery/CommercialAnimation/components/ListingCampaignCards/components/ListingCardImage';
import { BannerCampaign } from '@/productDiscovery/CommercialAnimation/interfaces/bannerCampaign';
import fetchedImgSrc from '@/sellerAdsManagement/PartnerAdBannerCampaign/assets/b2b-background.png';
import { PartnerAdBannerLabel } from '@/sellerAdsManagement/PartnerAdBannerCampaign/components/PartnerAdBannerLabel/PartnerAdBannerLabel';
import { defaultThemeColors } from '@/sellerAdsManagement/PartnerAdBannerCampaign/interfaces/themeColors';
import { usePartnerAdTheme } from '@/sellerAdsManagement/PartnerAdTheme/hooks/usePartnerAdTheme';

export interface ListingBannerProps {
  banner: BannerCampaign;
}

export const ListingBanner: FunctionComponent<ListingBannerProps> = ({
  banner,
}) => {
  const theme = usePartnerAdTheme();

  const themeColors = {
    '--banner-main-color':
      theme && theme.colors ? theme.colors.main : defaultThemeColors.main,
    '--banner-accent-color':
      theme && theme.colors ? theme.colors.accent : defaultThemeColors.accent,
  };

  const b2bStyle = isB2B() ? { backgroundImage: `url(${fetchedImgSrc})` } : '';

  return (
    <div
      className="relative h-full w-full rounded-card bg-[--banner-main-color]
        bg-blend-multiply"
      style={{ ...themeColors, ...b2bStyle }}
    >
      <Image
        width={SIZES.small.ldpi.width}
        height={SIZES.small.ldpi.height}
        loading="eager"
        alt={banner.image.altText}
        src={`${banner.image.mobileUrl}?w=${SIZES.small.ldpi.width}&h=${SIZES.small.ldpi.height}&fit=cover`}
        srcSet={`${banner.image.mobileUrl}?w=${SIZES.small.ldpi.width}&h=${SIZES.small.ldpi.height}&fit=cover 1x, ${banner.image.mobileUrl}?w=${SIZES.small.hdpi.width}&h=${SIZES.small.hdpi.height}&fit=cover 2x`}
        className="block h-full w-full rounded-card object-cover"
      >
        <source
          srcSet={`${banner.image.desktopUrl}?w=${SIZES.large.ldpi.width}&h=${SIZES.large.ldpi.height}&fit=cover&format=webp 1x, ${banner.image.desktopUrl}?w=${SIZES.large.hdpi.width}&h=${SIZES.large.hdpi.height}&fit=cover&format=webp 2x`}
          media={`(min-width: ${BREAKPOINTS.large}px)`}
          type="image/webp"
        />
        <source
          srcSet={`${banner.image.desktopUrl}?w=${SIZES.large.ldpi.width}&h=${SIZES.large.ldpi.height}&fit=cover 1x, ${banner.image.desktopUrl}?w=${SIZES.large.hdpi.width}&h=${SIZES.large.hdpi.height}&fit=cover 2x`}
          media={`(min-width: ${BREAKPOINTS.large}px)`}
        />
        <source
          srcSet={`${banner.image.desktopUrl}?w=${SIZES.medium.ldpi.width}&h=${SIZES.medium.ldpi.height}&fit=cover&format=webp 1x, ${banner.image.desktopUrl}?w=${SIZES.medium.hdpi.width}&h=${SIZES.medium.hdpi.height}&fit=cover&format=webp 2x`}
          media={`(min-width: ${BREAKPOINTS.medium}px)`}
          type="image/webp"
        />
        <source
          srcSet={`${banner.image.desktopUrl}?w=${SIZES.medium.ldpi.width}&h=${SIZES.medium.ldpi.height}&fit=cover 1x, ${banner.image.desktopUrl}?w=${SIZES.medium.hdpi.width}&h=${SIZES.medium.hdpi.height}&fit=cover 2x`}
          media={`(min-width: ${BREAKPOINTS.medium}px)`}
        />
        <source
          srcSet={`${banner.image.mobileUrl}?w=${SIZES.small.ldpi.width}&h=${SIZES.small.ldpi.height}&fit=cover&format=webp 1x, ${banner.image.mobileUrl}?w=${SIZES.small.hdpi.width}&h=${SIZES.small.hdpi.height}&fit=cover&format=webp 2x`}
          type="image/webp"
        />
      </Image>
      {banner.label && (
        <div
          className="absolute left-[70px] top-0 flex h-[223px] w-[205px]
            flex-col rounded-b-container bg-[--banner-accent-color] p-xs"
        >
          <PartnerAdBannerLabel
            label={banner.label}
            subtitleClassName="mt-s"
            lineClassName="font-bold whitespace-nowrap overflow-hidden text-ellipsis"
            middleLineClassName="text-[24px]"
            smallLineClassName="text-[20px]"
            bigLineClassName="text-[48px]"
            labelClassName="flex flex-col my-auto mx-s text-[--banner-main-color]"
            secondLineClassName={twMerge(
              banner.label.format === 'BIG_SMALL' && 'mt-[-5px]',
              banner.label.format === 'SMALL_BIG' && 'mt-[-10px]',
            )}
            subtitle={banner.subtitle}
          />
          {banner.logoUrl && (
            <div
              className="mt-auto flex h-[55px] w-full justify-center
                rounded-container bg-foundation-inverted p-s"
            >
              <Image
                className="h-auto max-h-full w-auto max-w-full object-contain"
                src={banner.logoUrl}
                alt="brand-logo"
                height={undefined}
                width={undefined}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ListingBanner.displayName = 'ListingBanner';
